import React, { useState, useEffect } from "react";
import AsyncSelect from "react-select/async";
import { useParams, useNavigate } from "react-router-dom";
import ApiClient from "../api/ApiClient";

const pageSizeOptions = [10, 25, 100, 500];

export const Candles = () => {
  const { tickerId } = useParams();
  const navigate = useNavigate();

  // suggestions are loaded asynchronously via react-select AsyncSelect
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [source, setSource] = useState("");

  const [interval, setInterval] = useState("Day");
  const [aggregated, setAggregated] = useState(false);
  const [orderBy, setOrderBy] = useState("Time");

  const [timeFrom, setTimeFrom] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    // return value suitable for input[type=datetime-local]
    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  });
  const [timeTo, setTimeTo] = useState(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  });

  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sources, setSources] = useState([]);

  useEffect(() => {
    ApiClient.getSources()
      .then(srcs => setSources(srcs))
      .catch(err => console.error('Failed to fetch sources:', err));
  }, []);

  useEffect(() => {
    if (!tickerId) return;
    ApiClient.searchTickerItems({ SearchTerm: `#${tickerId}`, Limit: 1, Offset: 0 })
      .then(results => {
        if (results && results.length > 0) {
          setSelectedTicker(results[0]);
        }
      })
      .catch(err => console.error('Failed to fetch ticker by id:', err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickerId]);

  async function loadOptions(inputValue) {
    if (!inputValue || inputValue.length < 2) {
      return [];
    }
    try {
      const body = { SearchTerm: inputValue, Source: source || undefined, Limit: 20, Offset: 0 };
      const data = await ApiClient.searchTickerItems(body);
      return (data || []).map((d) => ({
        value: d,
        label: `${d.Code || ''}${d.Name ? ' ' + d.Name : ''} [${d.Source || ''}]`,
      }));
    } catch (err) {
      console.error("Suggestions failed", err);
      return [];
    }
  }

  async function showData() {
    if (!selectedTicker) {
      alert("Please select a ticker");
      return;
    }
    setPage(0);
    // fetch first page
    await fetchData(0, pageSize, selectedTicker);
  }

  async function fetchData(p, ps, ticker) {
    setLoading(true);
    try {
      const body = {
        TickerId: ticker.Id,
        Interval: interval,
        Aggregated: aggregated,
        OrderBy: orderBy,
        TimeFrom: timeFrom ? new Date(timeFrom).toISOString() : null,
        TimeTo: timeTo ? new Date(timeTo).toISOString() : null,
        Limit: ps,
        Offset: p * ps,
      };

      // Backend pagination: POST /api/candles/search
      const res = await ApiClient.searchCandles(body);
      // res.items is array of Candle instances for this page
      setItems(res.Items);
      setTotal(res.TotalCount);
    } catch (err) {
      console.error("Loading candles failed", err);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  // Re-fetch when paging, ticker, interval or time range changes
  useEffect(() => {
    if (!selectedTicker) return;
    fetchData(page, pageSize, selectedTicker);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, selectedTicker, interval, aggregated, orderBy, timeFrom, timeTo]);

  function renderTable() {
    if (loading)
      return (
        <p>
          <em>Loading...</em>
        </p>
      );
    if (!items || items.length === 0) return <p>No results</p>;

    // items already represent the current page (backend pagination)
    return (
      <table className="table table-striped">
        <thead>
          <tr>
            <th>Id</th>
            <th>Time</th>
            <th>Open</th>
            <th>Close</th>
            <th>High</th>
            <th>Low</th>
            <th>Volume</th>
            <th>Realtime</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c, idx) => (
            <tr key={idx}>
              <td>{c.Id}</td>
              <td>{c.getTime()}</td>
              <td>{c.Open}</td>
              <td>{c.Close}</td>
              <td>{c.High}</td>
              <td>{c.Low}</td>
              <td>{c.Volume}</td>
              <td>{c.Realtime ? <span className="badge badge-warning" style={{ color: '#000' }}>Yes</span> : <span className="badge badge-secondary" style={{ color: '#000' }}>No</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));

  return (
    <div>
      <h1>Candles</h1>

      <div className="mb-3" style={{ maxWidth: "900px" }}>
        <label className="col-form-label">Source</label>
        <select
          className="form-control mb-2"
          value={source}
          onChange={(e) => setSource(e.target.value)}
        >
          <option value="">&lt;Any&gt;</option>
          {sources.map((src) => (
            <option key={src} value={src}>
              {src}
            </option>
          ))}
        </select>

        <label className="col-form-label">Ticker</label>
        <AsyncSelect
          cacheOptions
          loadOptions={loadOptions}
          defaultOptions={false}
          onChange={(opt) => {
            if (!opt) {
              setSelectedTicker(null);
              navigate('/candles');
              return;
            }
            setSelectedTicker(opt.value);
            navigate(`/candles/ticker/${opt.value.Id}`);
          }}
          value={
            selectedTicker
              ? {
                  value: selectedTicker,
                  label: `${(selectedTicker.Code || '')} - ${(selectedTicker.Name || '')}`,
                }
              : null
          }
          styles={{
            menu: (provided) => ({
              ...provided,
              zIndex: 1000,
              maxHeight: "300px",
            }),
          }}
        />

        <div className="mt-2 d-flex" style={{ gap: "8px" }}>
          <select
            className="form-control"
            style={{ width: "200px" }}
            value={aggregated ? `${interval}_aggregated` : interval}
            onChange={(e) => {
              const value = e.target.value;
              if (value.endsWith('_aggregated')) {
                setInterval(value.replace('_aggregated', ''));
                setAggregated(true);
              } else {
                setInterval(value);
                setAggregated(false);
              }
            }}
          >
            <option value="Minute">1 min</option>
            <option value="FiveMinutes">5 min</option>
            <option value="FiveMinutes_aggregated">5 min (aggregated)</option>
            <option value="QuarterHour">15 min</option>
            <option value="QuarterHour_aggregated">15 min (aggregated)</option>
            <option value="Hour">1 hr</option>
            <option value="Hour_aggregated">1 hr (aggregated)</option>
            <option value="Day">Daily</option>
            <option value="Week">Weekly</option>
            <option value="Month">Monthly</option>
          </select>
          <select
            className="form-control"
            style={{ width: "150px" }}
            value={orderBy}
            onChange={(e) => setOrderBy(e.target.value)}
          >
            <option value="Id">Id ASC</option>
            <option value="IdDesc">Id DESC</option>
            <option value="Time">Time ASC</option>
            <option value="TimeDesc">Time DESC</option>
          </select>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <div>
              <input
                type="datetime-local"
                className="form-control"
                value={timeFrom}
                onChange={(e) => setTimeFrom(e.target.value)}
              />
            </div>
            -
            <div>
              <input
                type="datetime-local"
                className="form-control"
                value={timeTo}
                onChange={(e) => setTimeTo(e.target.value)}
              />
            </div>
            <div style={{ display: "flex", alignItems: "end" }}>
              <button className="btn btn-primary" onClick={showData}>
                Show Data
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-2 d-flex align-items-center">
        <div className="mr-3">Page size:</div>
        <select
          className="form-control mr-3"
          style={{ width: "120px" }}
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(0);
          }}
        >
          {pageSizeOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <div>
          Page: {page + 1} / {totalPages || 1}
        </div>
      </div>

      <div className="mb-3 d-flex" style={{ gap: "8px", alignItems: "center" }}>
        <button
          className="btn btn-secondary"
          disabled={page <= 0}
          onClick={() => setPage(0)}
        >
          First
        </button>
        <button
          className="btn btn-secondary"
          disabled={page <= 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          Previous
        </button>
        <button
          className="btn btn-secondary"
          disabled={page + 1 >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
        >
          Next
        </button>
        <button
          className="btn btn-secondary"
          disabled={page + 1 >= totalPages}
          onClick={() => setPage(totalPages - 1)}
        >
          Last
        </button>
      </div>

      {renderTable()}

      <div className="mt-2 d-flex" style={{ gap: "8px" }}>
        <button
          className="btn btn-secondary"
          disabled={page <= 0}
          onClick={() => setPage(0)}
        >
          First
        </button>
        <button
          className="btn btn-secondary"
          disabled={page <= 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          Previous
        </button>
        <button
          className="btn btn-secondary"
          disabled={page + 1 >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
        >
          Next
        </button>
        <button
          className="btn btn-secondary"
          disabled={page + 1 >= totalPages}
          onClick={() => setPage(totalPages - 1)}
        >
          Last
        </button>
      </div>
    </div>
  );
};

export default Candles;
